import { useCallback } from 'react';
import { useBroadcastMutations } from '../queries/useBroadcastMutations';
import { useStanRouter } from '@/hooks/useStanRouter';
import { stanNotify } from '@/lib';
import type { ScheduleData } from '../components/ScheduleBroadcastModal';
import type { BroadcastFormData } from '../validations/broadcastForm';

interface UseBroadcastFormActionsProps {
    flowId: number;
    emailId: number;
}

export const useBroadcastFormActions = ({ flowId, emailId }: UseBroadcastFormActionsProps) => {
    const { push } = useStanRouter();
    const { saveBroadcast, sendBroadcast } = useBroadcastMutations();

    const handleSaveDraft = useCallback(
        async (formData: BroadcastFormData) => {
            await saveBroadcast.mutateAsync({
                flowId,
                emailId,
                data: formData,
                status: 'draft',
            });

            stanNotify({
                title: 'Success',
                text: 'Broadcast saved as draft',
                type: 'success',
            });

            push('/flows?tab=broadcasts');
        },
        [saveBroadcast, flowId, emailId, push]
    );

    const handleSendNow = useCallback(
        async (formData: BroadcastFormData) => {
            // Save as 'draft' before sending
            // This prevents the backend scheduler from picking up the broadcast
            // as a scheduled send while we're sending it immediately
            const result = await saveBroadcast.mutateAsync({
                flowId,
                emailId,
                data: formData,
                status: 'draft',
            });

            const combinedTags = [...formData.tags, ...formData.excludedTags.map(tag => ({ ...tag, excluded: 1 }))];

            await sendBroadcast.mutateAsync({
                flowId: result.flowId,
                emailId: result.emailId,
                tags: combinedTags,
            });

            stanNotify({
                title: 'Success',
                text: 'Broadcast sent successfully!',
                type: 'success',
            });

            push('/flows?tab=broadcasts');
        },
        [saveBroadcast, sendBroadcast, flowId, emailId, push]
    );

    const handleSchedule = useCallback(
        async (formData: BroadcastFormData, schedule: ScheduleData) => {
            await saveBroadcast.mutateAsync({
                flowId,
                emailId,
                data: formData,
                status: 'published',
                schedule,
            });

            stanNotify({
                title: 'Success',
                text: 'Broadcast scheduled successfully',
                type: 'success',
            });

            push('/flows?tab=broadcasts');
        },
        [saveBroadcast, flowId, emailId, push]
    );

    const isPending = saveBroadcast.isPending || sendBroadcast.isPending;

    return {
        handleSaveDraft,
        handleSendNow,
        handleSchedule,
        isPending,
    };
};
